import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  computed,
  forwardRef,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Valeur portée par une option du select. */
export type SelectValue = string | number | null;

/** Option affichée dans le panneau du select. */
export interface IdraSelectOption {
  label: string;
  value: SelectValue;
  disabled?: boolean;
}

/**
 * Liste déroulante du kit interne : déclencheur au style .idra-input et
 * panneau d'options aux tokens du thème (contrairement au <select> natif,
 * dont le panneau échappe au thème — mode sombre inclus).
 *
 * Compatible formControlName / ngModel (ControlValueAccessor) et utilisable
 * seule via [value] / (valueChange).
 *
 * Usage :
 *  <idra-select
 *    [options]="[{ label: 'Tous', value: 'all' }]"
 *    [value]="filtre()"
 *    (valueChange)="filtre.set($event)"
 *    ariaLabel="Filtrer"
 *  />
 *
 * Pour une longue liste, activer la recherche :
 *  <idra-select [options]="pays" [searchable]="true" />
 */
@Component({
  selector: 'idra-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IdraSelect), multi: true },
  ],
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
  styles: `
    :host {
      display: block;
      position: relative;
    }

    .trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      text-align: left;
      cursor: pointer;
    }

    .trigger .placeholder {
      color: var(--idra-text-muted);
    }

    .chevron {
      flex: none;
      font-size: 12px;
      color: var(--idra-text-muted);
      transition: transform 0.15s ease;
    }

    .chevron.up {
      transform: rotate(180deg);
    }

    .panel {
      /* position: fixed + coordonnées inline : le panneau est ancré au
         déclencheur mais échappe à tout conteneur en overflow (ex. le corps
         d'une modale), qui sinon rognerait les options du bas. */
      position: fixed;
      z-index: 200;
      overflow-y: auto;
      padding: 6px;
      background: var(--idra-surface-overlay);
      border: 1px solid var(--idra-border);
      border-radius: var(--idra-radius-md);
      box-shadow: var(--idra-shadow-lg);
    }

    .search {
      position: sticky;
      top: 0;
      z-index: 1;
      margin-bottom: 6px;
      background: var(--idra-surface-overlay);
    }

    .search input {
      width: 100%;
    }

    .empty {
      padding: 8px 10px;
      color: var(--idra-text-muted);
      font-size: 14px;
    }

    .option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      width: 100%;
      padding: 8px 10px;
      border: 0;
      border-radius: var(--idra-radius-xs);
      background: transparent;
      color: var(--idra-text);
      font: inherit;
      font-size: 14px;
      text-align: left;
      cursor: pointer;
    }

    .option:hover:not(:disabled),
    .option.highlighted {
      background: var(--idra-surface-hover);
    }

    .option.selected {
      color: var(--idra-primary);
      font-weight: 700;
    }

    .option:disabled {
      color: var(--idra-text-muted);
      cursor: not-allowed;
    }

    .check {
      flex: none;
      font-size: 12px;
    }
  `,
  template: `
    <button
      #trigger
      type="button"
      class="idra-input trigger"
      role="combobox"
      aria-haspopup="listbox"
      [id]="inputId() ?? null"
      [attr.aria-expanded]="open()"
      [attr.aria-label]="ariaLabel() ?? null"
      [disabled]="disabled()"
      (click)="toggle()"
      (keydown)="onKeydown($event)"
      (blur)="onTouched()"
    >
      @if (selectedOption(); as selected) {
        <span class="label">{{ selected.label }}</span>
      } @else {
        <span class="placeholder">{{ placeholder() }}</span>
      }
      <i class="chevron pi pi-chevron-down" [class.up]="open()" aria-hidden="true"></i>
    </button>

    @if (open()) {
      <div
        class="panel"
        role="listbox"
        [style.left.px]="panelLeft()"
        [style.width.px]="panelWidth()"
        [style.max-height.px]="panelMaxHeight()"
        [style.top.px]="panelTop()"
        [style.bottom.px]="panelBottom()"
      >
        @if (searchable()) {
          <div class="search">
            <input
              #searchInput
              type="text"
              class="idra-input"
              [placeholder]="searchPlaceholder()"
              [value]="search()"
              [attr.aria-label]="searchPlaceholder()"
              (input)="onSearch($event)"
              (keydown)="onKeydown($event)"
            />
          </div>
        }
        @for (option of filteredOptions(); track option.value; let index = $index) {
          <button
            type="button"
            class="option"
            role="option"
            tabindex="-1"
            [class.selected]="option.value === value()"
            [class.highlighted]="index === highlighted()"
            [attr.aria-selected]="option.value === value()"
            [disabled]="option.disabled ?? false"
            (click)="pick(option)"
            (mouseenter)="highlighted.set(index)"
          >
            {{ option.label }}
            @if (option.value === value()) {
              <i class="check pi pi-check" aria-hidden="true"></i>
            }
          </button>
        } @empty {
          <p class="empty">{{ emptyMessage() }}</p>
        }
      </div>
    }
  `,
})
export class IdraSelect implements ControlValueAccessor {
  private host = inject(ElementRef<HTMLElement>);

  readonly options = input<IdraSelectOption[]>([]);
  readonly placeholder = input('Sélectionner…');
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly inputId = input<string | undefined>(undefined);

  /** Affiche un champ de recherche pour filtrer les options. */
  readonly searchable = input(false);
  readonly searchPlaceholder = input('Rechercher…');
  readonly emptyMessage = input('Aucun résultat');

  /** Valeur courante — utilisable en [value]/(valueChange) hors formulaire. */
  readonly value = model<SelectValue>(null);

  protected readonly disabled = signal(false);
  protected readonly open = signal(false);
  protected readonly highlighted = signal(-1);
  protected readonly search = signal('');

  /** Coordonnées (viewport) du panneau en position fixe. */
  protected readonly panelLeft = signal(0);
  protected readonly panelWidth = signal(0);
  protected readonly panelMaxHeight = signal(260);
  protected readonly panelTop = signal<number | null>(null);
  protected readonly panelBottom = signal<number | null>(null);

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  /** Repositionnement sur défilement (capture, pour capter le scroll d'un
   *  conteneur interne comme le corps d'une modale) et redimensionnement. */
  private readonly reposition = () => this.updatePosition();

  protected readonly selectedOption = computed(
    () => this.options().find((option) => option.value === this.value()) ?? null,
  );

  /** Options visibles après application du filtre de recherche. */
  protected readonly filteredOptions = computed(() => {
    const term = this.normalize(this.search().trim());
    if (!this.searchable() || !term) {
      return this.options();
    }
    return this.options().filter((option) => this.normalize(option.label).includes(term));
  });

  /** Normalise pour une recherche insensible à la casse et aux accents. */
  private normalize(text: string): string {
    return text
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/gu, '');
  }

  protected onChange: (value: SelectValue) => void = () => {};
  protected onTouched: () => void = () => {};

  protected toggle(): void {
    if (this.open()) {
      this.close();
    } else {
      this.openPanel();
    }
  }

  protected pick(option: IdraSelectOption): void {
    if (option.disabled) {
      return;
    }
    this.value.set(option.value);
    this.onChange(option.value);
    this.close();
  }

  protected onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (this.open()) {
          this.move(event.key === 'ArrowDown' ? 1 : -1);
        } else {
          this.openPanel();
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (!this.open()) {
          this.openPanel();
        } else {
          const option = this.filteredOptions()[this.highlighted()];
          if (option) {
            this.pick(option);
          }
        }
        break;
      case ' ':
        // En recherche, l'espace doit rester saisissable dans le champ.
        if (this.searchable() && this.open()) {
          break;
        }
        event.preventDefault();
        if (!this.open()) {
          this.openPanel();
        } else {
          const option = this.filteredOptions()[this.highlighted()];
          if (option) {
            this.pick(option);
          }
        }
        break;
      case 'Escape':
      case 'Tab':
        this.close();
        break;
    }
  }

  /** Referme le panneau au clic hors du composant. */
  protected onDocumentClick(event: Event): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  /** Met à jour le terme recherché et réinitialise la surbrillance. */
  protected onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
    const firstEnabled = this.filteredOptions().findIndex((option) => !option.disabled);
    this.highlighted.set(firstEnabled);
  }

  private openPanel(): void {
    this.search.set('');
    this.open.set(true);
    this.updatePosition();
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
    const selectedIndex = this.filteredOptions().findIndex(
      (option) => option.value === this.value(),
    );
    this.highlighted.set(selectedIndex);
    if (this.searchable()) {
      // Focus du champ de recherche une fois le panneau rendu.
      queueMicrotask(() => this.searchInput()?.nativeElement.focus());
    }
  }

  private close(): void {
    this.open.set(false);
    this.highlighted.set(-1);
    this.search.set('');
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
  }

  /**
   * Aligne le panneau sur le déclencheur (position viewport) et choisit le
   * sens d'ouverture selon l'espace disponible, en bornant la hauteur pour
   * qu'aucune option ne soit rognée par le bord de l'écran.
   */
  private updatePosition(): void {
    const el = this.trigger()?.nativeElement;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const margin = 6;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const dropUp = spaceBelow < 200 && spaceAbove > spaceBelow;
    const available = Math.floor(Math.max(dropUp ? spaceAbove : spaceBelow, 0));

    this.panelLeft.set(Math.round(rect.left));
    this.panelWidth.set(Math.round(rect.width));
    this.panelMaxHeight.set(Math.min(260, available));

    if (dropUp) {
      this.panelTop.set(null);
      this.panelBottom.set(Math.round(window.innerHeight - rect.top + margin));
    } else {
      this.panelBottom.set(null);
      this.panelTop.set(Math.round(rect.bottom + margin));
    }
  }

  /** Déplace la surbrillance en sautant les options désactivées. */
  private move(delta: number): void {
    const options = this.filteredOptions();
    if (options.length === 0) {
      this.highlighted.set(-1);
      return;
    }
    let index = this.highlighted();
    for (let step = 0; step < options.length; step++) {
      index = (index + delta + options.length) % options.length;
      if (!options[index].disabled) {
        this.highlighted.set(index);
        return;
      }
    }
  }

  writeValue(value: SelectValue): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: SelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
